"use client";

import { useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import { api, loadTokens } from "@/lib/api";
import {
  AdminMarketEditModal,
  type EditableMarket,
} from "@/components/AdminMarketEditModal";

/**
 * EPIC-030 STORY-1801 — admin-only affordance row on the public
 * market-detail page.
 *
 * 2026-05-12: replaced the translation-only link with an inline
 * "Edit market" button that opens a full-field editor modal
 * (AdminMarketEditModal). Translations are now one row inside the
 * same modal — admins no longer need to navigate to a separate
 * editor page to flip a title, description, image, resolution
 * source, or close/resolve timestamp.
 *
 * Auth model: probes /v1/me on mount; renders nothing for anonymous
 * viewers, non-admin users, or while the role is unresolved. The
 * server-rendered <MarketDetailPage> stays static; this small client
 * island gates discovery of the admin tools without making the whole
 * page CSR.
 */
export function AdminMarketActions({ market }: { market: EditableMarket }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    if (!loadTokens()) return;
    let cancelled = false;
    void api
      .me()
      .then((profile) => {
        if (!cancelled) setIsAdmin(profile.role === "admin");
      })
      .catch(() => {
        // Silent — non-admin viewers see nothing, same as expired
        // tokens. The /v1/me 401 path doesn't need to surface a banner
        // here.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!isAdmin) return null;

  return (
    <>
      <div
        className="mb-3 inline-flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2.5 py-1 text-[11px] text-[var(--color-text2)]"
        role="region"
        aria-label="Admin actions"
      >
        <span className="font-medium uppercase tracking-[0.6px] text-[var(--color-text3)]">
          Admin
        </span>
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          className="inline-flex items-center gap-1 font-medium text-[var(--color-text)] transition-colors hover:text-[var(--color-brand)]"
        >
          <Pencil size={13} strokeWidth={1.5} aria-hidden="true" />
          Edit market
        </button>
      </div>
      <AdminMarketEditModal
        market={market}
        open={editOpen}
        onClose={() => setEditOpen(false)}
      />
    </>
  );
}
